import { useCallback, useState } from 'react'
import { userSelector } from '../app/main/mainSlice'
import { useAppDispatch, useAppSelector } from '../app/store/hooks'
import { FETCH_USER_MEAL_PLAN } from '../app/graphql/queries'
import { useQuery } from '@apollo/client'
import { setuserMealPlanData } from '../app/mealPlan/mealPlan.slice'
import { Meal, Meal_Plan_Data } from '../components/mealPlanPage/mealPlan.types'
import { getDietArray } from '../components/newHomePage/utils/newHomePageUtils'
import { ingredientsObjectSelector } from '../app/commonSelector'


const filterMealsByAllergens = (meals: Meal[], ingredients: any, userAllergens: string[]): Meal[] => {
    if (!meals || meals.length === 0 || !userAllergens || userAllergens.length === 0) return meals;
   
    return meals.filter(meal => {
        return !meal.Recipes_E3.Recipe_Variations[0].ingredients.some(ingredientId => {
            const ingredientAllergens = ingredients[ingredientId]?.allergens;
            return ingredientAllergens?.length > 0 && ingredientAllergens.some((allergen: string) => userAllergens.includes(allergen));
        });
    });
};

const useFetchMealPlan = (onCompletedCustom?: (mealPlanId: string | null) => void) => {
    const [mealPlanData, setMealPlanData] = useState<Meal_Plan_Data | null>(null)
    const user = useAppSelector(userSelector)
    const userSettings = user?.userSettings;
    const allowedDiets = getDietArray(userSettings?.diet);
    const userAllergens = userSettings?.allergens || [];
    const ingredients = useAppSelector(ingredientsObjectSelector);

    const dispatch = useAppDispatch()
    const { loading, refetch: refetchUserMeals } = useQuery(FETCH_USER_MEAL_PLAN, {
        variables: {
            userId: user?.id,
            allowedDiets
        },
        skip: !user?.id || !ingredients || ingredients.length === 0,
        onCompleted: (data) => {
            if (data?.User_To_Meal_Plan[0]) {
                const meals = data.User_To_Meal_Plan[0].Meal_Plan?.Meals
                const filteredMeals = filterMealsByAllergens(meals, ingredients, userAllergens)
                const filteredMealPlanData = {
                    ...data.User_To_Meal_Plan[0],
                    Meal_Plan: {
                        ...data.User_To_Meal_Plan[0].Meal_Plan,
                        Meals: filteredMeals
                    },
                }
                dispatch(setuserMealPlanData(filteredMealPlanData))
                setMealPlanData(filteredMealPlanData)
                localStorage.setItem('mealPlanData', JSON.stringify(filteredMealPlanData));

                const mealPlanId = data?.User_To_Meal_Plan[0].meal_plan_id;
                if (onCompletedCustom) onCompletedCustom(mealPlanId)
            }
            else {
                setMealPlanData(null)
                if (onCompletedCustom) onCompletedCustom(null)
            }
        },
    })

    const refetch = async () => {
        const data = await refetchUserMeals();
        if (data?.data?.User_To_Meal_Plan[0]) {
            const meals = data.data.User_To_Meal_Plan[0].Meal_Plan?.Meals
            const filteredMeals = filterMealsByAllergens(meals, ingredients, userAllergens)
            const filteredMealPlanData = {
                ...data.data.User_To_Meal_Plan[0],
                Meal_Plan: {
                    ...data.data.User_To_Meal_Plan[0].Meal_Plan,
                    Meals: filteredMeals
                },
            }
            dispatch(setuserMealPlanData(filteredMealPlanData))
            setMealPlanData(filteredMealPlanData)
            localStorage.setItem('mealPlanData', JSON.stringify(filteredMealPlanData));

        }
        else
            setMealPlanData(null)
    }

    const updateMealServingSize = useCallback((mealId: string, servingSize: number) => {
        const newMeals = mealPlanData?.Meal_Plan?.Meals.map((meal: Meal) =>
            meal.id === mealId ? { ...meal, serving_size: servingSize } : meal
        );

        if (mealPlanData?.Meal_Plan?.Meals) setMealPlanData({
            ...mealPlanData,
            Meal_Plan: {
                ...mealPlanData.Meal_Plan,
                Meals: newMeals ?? []
            }
        })
    }, [mealPlanData])
    return { mealPlanData, loading, refetch, updateMealServingSize }
}

export default useFetchMealPlan
